'use client';

import type { ChangeEventHandler, FocusEventHandler } from 'react';
import { startTransition } from 'react';
import { useShallow } from 'zustand/shallow';
import { usePageAction } from '@repo/hooks/usePageAction';
import { APY_INPUT_PATTERN } from '../../constants';
import { ApyPercentCalculatorInput } from '../../shared/ApyPercentCalculatorInput';
import { handleAnnualApyInput } from './annualApyInput';
import { useHYSACalculatorStore } from './calculatorStore';
import { formatToDisplayValue, isAmbiguousAnnualApyString } from './utils';

export function AnnualPercentYieldForm() {
  const fireHysaCalculatorInteractedPageAction = usePageAction({
    actionName: 'hysa_calculator_interacted',
    actionSection: 'banking',
    actionType: 'select',
  });
  const {
    annualPercentageYield,
    annualPercentageYieldError,
    initialDeposit,
    monthlyDeposit,
    setField,
    timePeriod,
  } = useHYSACalculatorStore(
    useShallow(state => ({
      annualPercentageYield: state.formFields.annualPercentageYield,
      annualPercentageYieldError: state.errors.annualPercentageYield,
      initialDeposit: state.formFields.initialDeposit.includes(',')
        ? state.formFields.initialDeposit.replace(/,/g, '')
        : formatToDisplayValue(state.formFields.initialDeposit),
      monthlyDeposit: state.formFields.monthlyDeposit.includes(',')
        ? state.formFields.monthlyDeposit.replace(/,/g, '')
        : formatToDisplayValue(state.formFields.monthlyDeposit),
      setField: state.setField,
      timePeriod: state.formFields.timePeriod,
    })),
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    handleAnnualApyInput(e.target.value, {
      fireHysaCalculatorInteractedPageAction,
      initialDeposit,
      monthlyDeposit,
      timePeriod,
      setField,
      startTransition,
    });
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    const target = e.target.value;
    if (!APY_INPUT_PATTERN.test(target)) {
      return;
    }
    if (isAmbiguousAnnualApyString(target)) {
      handleAnnualApyInput(target.replace('.', ''), {
        fireHysaCalculatorInteractedPageAction,
        initialDeposit,
        monthlyDeposit,
        timePeriod,
        setField,
        startTransition,
      });
    }
  };

  return (
    <ApyPercentCalculatorInput
      error={annualPercentageYieldError}
      id="annual-percentage-yield"
      value={annualPercentageYield}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
